import { Edit, useSelect } from "@refinedev/antd";
import { useForm } from "@refinedev/antd";
import { useGetIdentity } from "@refinedev/core";
import { useNavigate, useParams, useLocation } from "react-router";
import { AUDITOR_USER_TYPES } from "../../types/auth";
import type { UserInfo } from "../../types/auth";
import {
  Form,
  Input,
  DatePicker,
  TimePicker,
  InputNumber,
  Select,
  Card,
  Row,
  Col,
  Button,
  Breadcrumb,
  Space,
  Switch,
} from "antd";
import {
  ArrowLeftOutlined,
  HomeOutlined,
  AuditOutlined,
  CalendarOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { PhoneInput } from "../../components/PhoneInput";
import { usePageTitle } from "../../hooks";
import { ContactReceiverSection } from "./components/ContactReceiverSection";
import { CreatableSelect } from "../../components/CreatableSelect";
import { packerService } from "../../services/api/packerService";
import { exporterService } from "../../services/api/exporterService";
import { decreaseReasonService } from "../../services/api/decreaseReasonService";

const { TextArea } = Input;

/**
 * AuditEditInfo Component
 * Form to edit audit information including dates, contact, production data
 */
export const AuditEditInfo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const { formProps, saveButtonProps, query } = useForm({
    action: "edit",
    resource: "audits",
    id,
    redirect: false,
    onMutationSuccess: () => {
      navigate(`/audits/${id}`);
    },
  });

  const audit = query?.data?.data as any;
  
  // Set page title
  usePageTitle(audit?.reference ? `${t("audit.edit_info", "Editar")} - ${audit.reference}` : t("audit.edit_info", "Editar Auditoría"));

  // Load business positions for the position selector
  const { selectProps: positionSelectProps } = useSelect({
    resource: "business-positions",
    optionLabel: "name",
    optionValue: "id",
  });

  // Load decrease reasons for the merma selector
  const { selectProps: decreaseReasonSelectProps } = useSelect({
    resource: "decrease-reasons",
    optionLabel: "name",
    optionValue: "id",
  });

  // Load packers for the etiquetador selector
  const { selectProps: packerSelectProps } = useSelect({
    resource: "packers",
    optionLabel: "name",
    optionValue: "id",
  });

  // Load exporters for the exportador selector
  const { selectProps: exporterSelectProps } = useSelect({
    resource: "exporters",
    optionLabel: "name",
    optionValue: "id",
  });

  // Transform data for form
  const initialValues = audit ? {
    ...audit,
    // `farm_name` lives only in the form — when changed, we dispatch
    // a separate PATCH to `/farms/{id}/rename/` so the audit edit
    // payload stays focused on audit fields.
    farm_name: audit.farm?.name ?? "",
    start_date: audit.start_date ? dayjs(audit.start_date) : null,
    start_time: audit.start_time ? dayjs(audit.start_time, "HH:mm:ss") : null,
    finishing_time: audit.finishing_time ? dayjs(audit.finishing_time, "HH:mm:ss") : null,
    harvest_date: audit.harvest_date ? dayjs(audit.harvest_date) : null,
    // Position is stored as text, not ID
    position: audit.position || undefined,
    // Transform packers and exporters from objects to IDs
    packers: audit.packers?.map((p: any) => p.id) || [],
    exporters: audit.exporters?.map((e: any) => e.id) || [],
    reason_for_decrease: audit.reason_for_decrease?.map((r: any) => r.id) || [],
    // Transform contact_receiver_unified from object to ID
    contact_receiver_unified: typeof audit.contact_receiver_unified === 'object' && audit.contact_receiver_unified?.id
      ? audit.contact_receiver_unified.id
      : audit.contact_receiver_unified || null,
  } : undefined;

  // Get navigation context from location state
  const fromPlanningId = (location.state as any)?.fromPlanningId;
  const planningName = (location.state as any)?.planningName;

  const { data: identity } = useGetIdentity<UserInfo>();
  const isAuditor = (AUDITOR_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  // Resolve the planning detail URL per role. The original code used
  // /planning-groups/${id} which isn't a valid admin route either
  // (the real one is /planning-groups/show/${id}).
  const planningDetailUrl = fromPlanningId
    ? isAuditor
      ? `/auditor/planning-groups/${fromPlanningId}`
      : `/planning-groups/show/${fromPlanningId}`
    : null;
  const planningsListUrl = isAuditor ? "/auditor/planning-groups" : "/planning-groups";

  const handleBack = () => {
    // Edit-info is always reached from /audits/:id show, so the
    // natural back target is the show page itself. The show page's
    // handleBack then takes the user further up (planning detail or
    // role-based fallback).
    navigate(`/audits/${id}`, { state: { fromPlanningId, planningName } });
  };

  // Build dynamic breadcrumb based on navigation context
  const getBreadcrumbItems = () => {
    const items: any[] = [
      {
        title: <HomeOutlined />,
        href: "/",
        onClick: (e: any) => {
          e.preventDefault();
          navigate("/");
        },
      },
    ];

    if (fromPlanningId && planningDetailUrl) {
      // Coming from planning group — links resolve per role.
      items.push({
        title: t("planning.title", "Planificaciones"),
        href: planningsListUrl,
        onClick: (e: any) => {
          e.preventDefault();
          navigate(planningsListUrl);
        },
      });
      items.push({
        title: planningName || `Planificación #${fromPlanningId}`,
        href: planningDetailUrl,
        onClick: (e: any) => {
          e.preventDefault();
          navigate(planningDetailUrl);
        },
      });
    } else if (isAuditor) {
      // Auditor without planning context — never link to /audits
      // (they can't see it). Route through their plannings list.
      items.push({
        title: (
          <>
            <CalendarOutlined /> {t("planning.title", "Planificaciones")}
          </>
        ),
        href: "/auditor/planning-groups",
        onClick: (e: any) => {
          e.preventDefault();
          navigate("/auditor/planning-groups");
        },
      });
    } else {
      // Admin path — full audits list crumb.
      items.push({
        title: (
          <>
            <AuditOutlined /> {t("audit.title", "Auditorías")}
          </>
        ),
        href: "/audits",
        onClick: (e: any) => {
          e.preventDefault();
          navigate("/audits");
        },
      });
    }

    items.push({
      title: audit?.reference || `Auditoría #${id}`,
      href: `/audits/${id}`,
      onClick: (e: any) => {
        e.preventDefault();
        navigate(`/audits/${id}`, { state: { fromPlanningId, planningName } });
      },
    });
    
    items.push({
      title: t("audit.edit_info", "Editar Información"),
    });

    return items;
  };

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      headerButtons={() => (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        >
          {t("common.back") || "Volver"}
        </Button>
      )}
      footerButtons={
        // Match the other audit forms: "Cancelar" + "Guardar"
        // side-by-side at the bottom-right. The default save
        // button from Refine ships with the loading state already
        // wired, so we spread `saveButtonProps` onto our own
        // primary button.
        <Space>
          <Button onClick={handleBack}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button {...saveButtonProps} type="primary" icon={<SaveOutlined />}>
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      }
      title={t("audit.edit_info") || "Editar Información de Auditoría"}
      breadcrumb={
        <Breadcrumb items={getBreadcrumbItems()} />
      }
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={initialValues}
        onFinish={async (values: any) => {
          // If the auditor edited the farm name, fire a separate
          // PATCH to /farms/{id}/rename/ so the change is logged in
          // FarmNameChange with this audit as the context. This is
          // dispatched BEFORE the audit PATCH so a rename failure
          // doesn't leave us with stale audit data on success.
          const farmId = audit?.farm?.id;
          const originalFarmName = audit?.farm?.name ?? "";
          const newFarmName = (values.farm_name ?? "").trim();
          if (farmId && newFarmName && newFarmName !== originalFarmName) {
            try {
              const { apiClient } = await import("../../services/api/apiClient");
              await apiClient.post(`/farms/${farmId}/rename/`, {
                name: newFarmName,
                audit_id: audit?.id ?? null,
              });
            } catch (err: any) {
              // Block the audit submit if the rename failed — the
              // user can correct the name and retry.
              // eslint-disable-next-line no-alert
              alert(
                err?.response?.data?.name?.[0] ??
                  t(
                    "audit.farm_rename_error",
                    "No se pudo actualizar el nombre de la finca",
                  ),
              );
              return;
            }
          }

          // farm_name doesn't belong to the audit payload.
          const { farm_name: _farmName, ...rest } = values;

          // Transform dates back to strings
          const transformedValues = {
            ...rest,
            start_date: rest.start_date ? dayjs(rest.start_date).format("YYYY-MM-DD") : null,
            start_time: rest.start_time ? dayjs(rest.start_time).format("HH:mm:ss") : null,
            finishing_time: rest.finishing_time ? dayjs(rest.finishing_time).format("HH:mm:ss") : null,
            harvest_date: rest.harvest_date ? dayjs(rest.harvest_date).format("YYYY-MM-DD") : null,
            // Ensure contact_receiver_unified is sent as ID only (not object)
            contact_receiver_unified: rest.contact_receiver_unified || null,
          };
          formProps.onFinish?.(transformedValues);
        }}
      >
        {/* Finca */}
        <Card title={t("audit.farm_info", "Finca")} style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("audit.farm_name", "Nombre de la finca")}
                name="farm_name"
                rules={[
                  { required: true, message: t("audit.farm_name_required", "El nombre de la finca es obligatorio") },
                  { max: 200 },
                ]}
                extra={t(
                  "audit.farm_name_scope_hint",
                  "Al modificar el nombre se actualiza la ficha de la finca, que se comparte con todas las auditorías del histórico. El cambio queda registrado con esta auditoría como referencia.",
                )}
              >
                <Input
                  placeholder={t("audit.farm_name_placeholder", "Nombre de la finca")}
                  maxLength={200}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Fechas y Horarios */}
        <Card title={t("audit.dates_times") || "Fechas y Horarios"} style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t("audit.start_date") || "Fecha Auditoría"}
                name="start_date"
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t("audit.start_time") || "Hora Inicio"}
                name="start_time"
              >
                <TimePicker style={{ width: "100%" }} format="HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t("audit.finishing_time") || "Hora Fin"}
                name="finishing_time"
              >
                <TimePicker style={{ width: "100%" }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Persona de Contacto */}
        {audit && (
          <ContactReceiverSection
            audit={audit}
            form={formProps.form}
            positionSelectProps={positionSelectProps}
          />
        )}

        {/* Croquis / Mapa de la finca */}
        <Card
          title={
            t("audit.map_check") || "Mapa de la finca coincide con la realidad"
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  t("audit.map_is_showing_reality")
                  || "El croquis coincide con la realidad"
                }
                name="map_is_showing_reality"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev.map_is_showing_reality !== cur.map_is_showing_reality
            }
          >
            {({ getFieldValue }) =>
              !getFieldValue("map_is_showing_reality") ? (
                <Form.Item
                  label={
                    t("audit.map_mismatch_reason")
                    || "Motivo por el que no coincide"
                  }
                  name="map_mismatch_reason"
                >
                  <Input.TextArea
                    rows={3}
                    placeholder={
                      t("audit.map_mismatch_reason_placeholder")
                      || "Describe por qué el croquis no se corresponde con lo que has visto en campo..."
                    }
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Card>

        {/* Información de Producción */}
        <Card title={t("audit.production_info") || "Información de Producción"} style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.packer") || "Etiquetadores"}
                name="packers"
              >
                <CreatableSelect
                  {...packerSelectProps}
                  mode="multiple"
                  placeholder={t("audit.packer_placeholder") || "Seleccionar etiquetadores"}
                  createText={t("audit.create_packer") || "Crear nuevo etiquetador"}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onCreateNew={async (name) => {
                    const newPacker = await packerService.createPacker(name);
                    return newPacker;
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.exporter") || "Exportadores"}
                name="exporters"
              >
                <CreatableSelect
                  {...exporterSelectProps}
                  mode="multiple"
                  placeholder={t("audit.exporter_placeholder") || "Seleccionar exportadores"}
                  createText={t("audit.create_exporter") || "Crear nuevo exportador"}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onCreateNew={async (name) => {
                    const newExporter = await exporterService.createExporter(name);
                    return newExporter;
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.estimated_production_producer") || "Producción Estimada Productor (kg)"}
                name="stimated_production"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.harvest_date") || "Fecha Estimada de Corta"}
                name="harvest_date"
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.decrease_reason") || "Motivos de Merma"}
                name="reason_for_decrease"
              >
                <CreatableSelect
                  {...decreaseReasonSelectProps}
                  mode="multiple"
                  placeholder={t("audit.decrease_reason_placeholder") || "Seleccionar motivos de merma"}
                  createText={t("audit.create_decrease_reason") || "Crear nuevo motivo de merma"}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onCreateNew={async (name) => {
                    const newReason = await decreaseReasonService.createDecreaseReason(name);
                    return newReason;
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.decrease_percentage") || "% de Merma"}
                name="decrease_percentage"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  placeholder="0"
                  formatter={(value) => `${value}%`}
                  parser={(value) => value?.replace('%', '') as any}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.is_under_net") || "Finca Mallada"}
                name="is_under_net"
              >
                <Select
                  placeholder={t("audit.select_option") || "Seleccionar"}
                  allowClear
                >
                  <Select.Option value="si">{t("common.yes") || "Sí"}</Select.Option>
                  <Select.Option value="parcial">{t("audit.partial") || "Parcial"}</Select.Option>
                  <Select.Option value="no">{t("common.no") || "No"}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("audit.production_type") || "Tipo de Producción"}
                name="production_type"
              >
                <Select
                  placeholder={t("audit.select_option") || "Seleccionar"}
                  allowClear
                >
                  <Select.Option value="convencional">Convencional</Select.Option>
                  <Select.Option value="transformacion">En Conversión</Select.Option>
                  <Select.Option value="eco">ECO</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Comentarios */}
        <Card title={t("audit.comments") || "Comentarios"} style={{ marginBottom: 24 }}>
          <Form.Item
            label={t("audit.comments") || "Comentarios de Auditoría"}
            name="comments"
          >
            <TextArea
              rows={4}
              maxLength={2000}
              placeholder={t("audit.comments_placeholder") || "Ingrese comentarios u observaciones sobre la auditoría"}
              showCount
            />
          </Form.Item>
        </Card>
      </Form>
    </Edit>
  );
};
